import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DatePicker from 'react-datepicker';
import ReCAPTCHA from 'react-google-recaptcha';
import 'react-datepicker/dist/react-datepicker.css';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import ScrollingLogo from '@/components/ScrollingLogo';
import { 
  Building2, Users, Calendar, MapPin, Star, ChevronRight, 
  MessageCircle, Send, X, ArrowRight, Quote, CheckCircle2,
  Hotel, Trophy, Clock, Shield, Menu, Eye, EyeOff
} from 'lucide-react';

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function LandingPage() {
  const { user, setAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'otp'
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpPurpose, setOtpPurpose] = useState('login'); // 'login' or 'signup'
  const [searchCheckIn, setSearchCheckIn] = useState(null);
  const [searchCheckOut, setSearchCheckOut] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const [showAllTestimonials, setShowAllTestimonials] = useState(false);
  const [isTestimonialPaused, setIsTestimonialPaused] = useState(false);
  const [checkInViewedMonth, setCheckInViewedMonth] = useState(new Date());
  const [checkOutViewedMonth, setCheckOutViewedMonth] = useState(new Date());
  
  // Search form state
  const [searchLocation, setSearchLocation] = useState('');
  const [searchEventType, setSearchEventType] = useState('');
  const [searchAttendees, setSearchAttendees] = useState('50');
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  
  // Check for login parameter to auto-open login dialog
  useEffect(() => {
    if (searchParams.get('login') === 'true' && !user) {
      setShowLoginDialog(true);
    }
  }, [searchParams, user]);
  
  // Data states
  const [bookingFeed, setBookingFeed] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({});
  const [featuredProperties, setFeaturedProperties] = useState([]);
  
  // Chat states
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  
  // WhatsApp Configuration
  const WHATSAPP_NUMBER = '+919664870766'; // My Corporate Hub WhatsApp
  const WHATSAPP_MESSAGE = 'Hi! I need help with corporate event booking.';
  
  const openWhatsApp = () => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
    window.open(url, '_blank');
  };

  // Handle Get Instant Quote - validates form and checks login
  const handleGetInstantQuote = (e) => {
    e.preventDefault();
    
    // Validate that all required fields are filled
    if (!searchLocation) {
      toast.error('Please select a location');
      return;
    }
    if (!searchEventType) {
      toast.error('Please select an event type');
      return;
    }
    if (!searchCheckIn) {
      toast.error('Please select a check-in date');
      return;
    }
    if (!searchCheckOut) {
      toast.error('Please select a check-out date');
      return;
    }
    if (!searchAttendees || parseInt(searchAttendees) < 1) {
      toast.error('Please enter number of attendees');
      return;
    }

    // Check if user is logged in
    if (!user) {
      toast.info('Please login to submit your RFP');
      setShowLoginDialog(true);
      return;
    }

    // User is logged in and form is valid - redirect to RFP page with pre-filled data
    const queryParams = new URLSearchParams({
      location: searchLocation,
      event_type: searchEventType,
      check_in: searchCheckIn.toISOString().split('T')[0],
      check_out: searchCheckOut.toISOString().split('T')[0],
      attendees: searchAttendees
    }).toString();
    
    toast.success('Redirecting to RFP form with your details...');
    navigate(`/bulk-rfp/new?${queryParams}`);
  };

  // Scroll effect for transparent header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check for showLogin query param (from redirects)
  useEffect(() => {
    if (searchParams.get('showLogin') === 'true') {
      setShowLoginDialog(true);
      // Remove the query param from URL without triggering navigation
      window.history.replaceState({}, '', '/');
    }
  }, [searchParams]);

  // Auto-play testimonials - advances every 5 seconds, controlled by pause button
  useEffect(() => {
    if (isTestimonialPaused) return; // Don't auto-advance when paused
    
    const interval = setInterval(() => {
      setActiveTestimonialIndex(prev => 
        prev === clientTestimonials.length - 1 ? 0 : prev + 1
      );
    }, 5000); // Change every 5 seconds
    
    return () => clearInterval(interval);
  }, [isTestimonialPaused]);

  useEffect(() => {
    fetchPublicData();
  }, []);

  const fetchPublicData = async () => {
    try {
      const [feedRes, testimonialsRes, statsRes, propertiesRes] = await Promise.all([
        api.getBookingFeed(),
        api.getApprovedTestimonials(),
        api.getPlatformStats(),
        api.getProperties()
      ]);
      setBookingFeed(feedRes.data);
      setTestimonials(testimonialsRes.data);
      setStats(statsRes.data);
      setFeaturedProperties(propertiesRes.data.filter(p => p.featured).slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validate before submitting
    const email = loginEmail.trim();
    const password = loginPassword.trim();
    
    console.log('Login attempt:', { email, password: password ? '****' : 'empty' });
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('Sending login request...');
      const res = await api.login({ email, password });
      console.log('Login success:', res.data);
      setAuth(res.data.token, res.data.user);
      setShowLoginDialog(false);
      setLoginEmail('');
      setLoginPassword('');
      toast.success('Welcome back!');
      
      // Navigate based on role
      const role = res.data.user.role;
      if (role === 'admin') navigate('/dashboard/admin');
      else if (role === 'vendor') navigate('/dashboard/vendor');
      else navigate('/dashboard/client');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e?.preventDefault();
    
    const email = loginEmail.trim();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setIsLoading(true);
    try {
      const res = await api.post('/auth/send-otp', {
        email,
        purpose: otpPurpose
      });
      
      setOtpSent(true);
      toast.success(`OTP sent to ${email}. Check your email!`);
      
      // Show OTP in console for demo (remove in production)
      if (res.data.otp_for_demo) {
        console.log('🔐 Demo OTP:', res.data.otp_for_demo);
        toast.info(`Demo OTP: ${res.data.otp_for_demo}`, { duration: 10000 });
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    const email = loginEmail.trim();
    const otp = otpCode.trim();
    
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setIsLoading(true);
    try {
      const res = await api.post('/auth/verify-otp', {
        email,
        otp,
        purpose: otpPurpose
      });
      
      if (res.data.success) {
        if (otpPurpose === 'login') {
          // Login successful
          setAuth(res.data.token, res.data.user);
          setShowLoginDialog(false);
          setLoginEmail('');
          setOtpCode('');
          setOtpSent(false);
          toast.success('Login successful!');
          
          // Navigate based on role
          const role = res.data.user.role;
          if (role === 'admin') navigate('/dashboard/admin');
          else if (role === 'vendor') navigate('/dashboard/vendor');
          else navigate('/dashboard/client');
        } else if (otpPurpose === 'signup') {
          toast.success('Email verified! You can now complete registration.');
          // In a full implementation, show registration form here
        }
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    if (!user) {
      toast.error('Please login to use the AI assistant');
      setShowLoginDialog(true);
      return;
    }

    const userMessage = chatInput;
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setIsChatLoading(true);

    try {
      const res = await api.sendAIChat({ message: userMessage, session_id: sessionId });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      setSessionId(res.data.session_id);
    } catch (error) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
  };

  // MNC Partners - Corporate client logos (all uploaded by user)
  const trustedLogos = [
    { name: 'Infosys', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/95/Infosys_logo.svg/200px-Infosys_logo.svg.png' },
    { name: 'Malabar Gold & Diamonds', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/b75oekmp_1.jpeg' },
    { name: 'HDFC Bank', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/28/HDFC_Bank_Logo.svg/200px-HDFC_Bank_Logo.svg.png' },
    { name: 'Wipro', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/Wipro_Primary_Logo_Color_RGB.svg/200px-Wipro_Primary_Logo_Color_RGB.svg.png' },
    { name: 'AngelOne', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/prkp14ut_1697189114-5702.jpg.webp' },
    { name: 'TCS', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/rq8zcoa1_Tata_Consultancy_Services_old_logo.svg.png' },
    { name: 'Larsen & Toubro', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/i7in3vt2_logo.jpg.webp' },
    { name: 'ICICI Bank', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/12/ICICI_Bank_Logo.svg/200px-ICICI_Bank_Logo.svg.png' },
    { name: 'Mahindra', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/fnhbfy5u_85173157.cms.avif' },
    { name: 'Airtel', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/bieew5hy_airtel-logo-vector.png' },
    { name: 'HCL Technologies', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/usorz3pt_HCL-Logo-1976.png' },
    { name: 'HDFC Life', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/0zfyisen_HDFCLIFE.NS_BIG-38e723f5-2.png' },
    { name: 'Aditya Birla Group', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/ttltx5vx_Aditya_Birla_Group_Logo.svg.png' },
    { name: 'HSBC', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/13x3drhp_2560px-HSBC_logo_%282018%29.svg.png' },
    { name: 'IndiaFirst Life', logo: 'https://customer-assets.emergentagent.com/job_bookingapp-fix/artifacts/sm4gecwp_ifli-logo-new.e3f60dedb7ad95a1b440.png' }
  ];

  // Hotel Partners - Featured partners with stats and tiers
  const hotelPartners = [
    { 
      name: 'Taj Hotels', 
      logo: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=400&h=300&fit=crop',
      tier: 'Platinum',
      venues: 85,
      cities: 12,
      description: 'India\'s iconic luxury hotel chain'
    },
    { 
      name: 'The Oberoi Group', 
      logo: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=400&h=300&fit=crop',
      tier: 'Platinum',
      venues: 42,
      cities: 8,
      description: 'World-renowned luxury hospitality'
    },
    { 
      name: 'ITC Hotels', 
      logo: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop',
      tier: 'Platinum',
      venues: 65,
      cities: 15,
      description: 'Responsible luxury redefined'
    },
    { 
      name: 'The Leela Palaces', 
      logo: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop',
      tier: 'Gold',
      venues: 28,
      cities: 6,
      description: 'Ultra-luxury palace hotels'
    },
    { 
      name: 'Marriott International', 
      logo: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=400&h=300&fit=crop',
      tier: 'Gold',
      venues: 120,
      cities: 25,
      description: 'Global hospitality leader'
    },
    { 
      name: 'Hyatt Hotels', 
      logo: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400&h=300&fit=crop',
      tier: 'Gold',
      venues: 45,
      cities: 10,
      description: 'Premium business hotels'
    },
    { 
      name: 'Radisson Hotel Group', 
      logo: 'https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=400&h=300&fit=crop',
      tier: 'Silver',
      venues: 78,
      cities: 18,
      description: 'International hotel company'
    },
    { 
      name: 'Hilton Hotels', 
      logo: 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=400&h=300&fit=crop',
      tier: 'Silver',
      venues: 55,
      cities: 14,
      description: 'Trusted worldwide hospitality'
    },
    { 
      name: 'Accor Hotels', 
      logo: 'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=400&h=300&fit=crop',
      tier: 'Silver',
      venues: 62,
      cities: 16,
      description: 'French hospitality excellence'
    }
  ];

  // Static Real-Time Bookings Data - 45 different bookings
  const liveBookings = [
    { company_name: 'Tata Consultancy Services', rooms_count: 150, property_name: 'Taj Mahal Palace', city: 'Mumbai', event_type: 'Annual Conference' },
    { company_name: 'Infosys Limited', rooms_count: 85, property_name: 'ITC Grand Chola', city: 'Chennai', event_type: 'Leadership Summit' },
    { company_name: 'Wipro Technologies', rooms_count: 200, property_name: 'The Oberoi', city: 'Bangalore', event_type: 'Tech Summit' },
    { company_name: 'Reliance Industries', rooms_count: 300, property_name: 'JW Marriott', city: 'Mumbai', event_type: 'Board Meeting' },
    { company_name: 'HDFC Bank', rooms_count: 120, property_name: 'The Leela Palace', city: 'Delhi', event_type: 'Training Program' },
    { company_name: 'Mahindra & Mahindra', rooms_count: 95, property_name: 'Hyatt Regency', city: 'Pune', event_type: 'Product Launch' },
    { company_name: 'Larsen & Toubro', rooms_count: 180, property_name: 'ITC Maurya', city: 'Delhi', event_type: 'Annual Gala' },
    { company_name: 'Bajaj Finserv', rooms_count: 75, property_name: 'Radisson Blu', city: 'Pune', event_type: 'Sales Meet' },
    { company_name: 'Hindustan Unilever', rooms_count: 110, property_name: 'Taj Lands End', city: 'Mumbai', event_type: 'Brand Summit' },
    { company_name: 'Asian Paints', rooms_count: 65, property_name: 'The Westin', city: 'Gurgaon', event_type: 'Dealer Meet' },
    { company_name: 'Tech Mahindra', rooms_count: 140, property_name: 'Marriott Suites', city: 'Hyderabad', event_type: 'Hackathon' },
    { company_name: 'Godrej Industries', rooms_count: 90, property_name: 'Hilton', city: 'Mumbai', event_type: 'Innovation Day' },
    { company_name: 'Adani Group', rooms_count: 250, property_name: 'Taj Exotica', city: 'Goa', event_type: 'Leadership Retreat' },
    { company_name: 'Cipla Pharma', rooms_count: 80, property_name: 'Four Seasons', city: 'Mumbai', event_type: 'Medical Conference' },
    { company_name: 'Kotak Mahindra Bank', rooms_count: 100, property_name: 'The Oberoi Udaivilas', city: 'Udaipur', event_type: 'Rewards Ceremony' },
    // New 30 bookings
    { company_name: 'ICICI Bank', rooms_count: 135, property_name: 'Taj Palace', city: 'Delhi', event_type: 'Banking Summit' },
    { company_name: 'HCL Technologies', rooms_count: 175, property_name: 'The Leela Ambience', city: 'Gurgaon', event_type: 'Developer Conference' },
    { company_name: 'Bharti Airtel', rooms_count: 220, property_name: 'ITC Grand Central', city: 'Mumbai', event_type: 'Telecom Expo' },
    { company_name: 'Sun Pharma', rooms_count: 95, property_name: 'Taj Falaknuma Palace', city: 'Hyderabad', event_type: 'R&D Workshop' },
    { company_name: 'Maruti Suzuki', rooms_count: 160, property_name: 'The Oberoi Gurgaon', city: 'Gurgaon', event_type: 'Auto Summit' },
    { company_name: 'Axis Bank', rooms_count: 110, property_name: 'Grand Hyatt', city: 'Mumbai', event_type: 'Fintech Forum' },
    { company_name: 'JSW Steel', rooms_count: 85, property_name: 'Taj West End', city: 'Bangalore', event_type: 'Industry Meet' },
    { company_name: 'Nestle India', rooms_count: 70, property_name: 'Hyatt Centric', city: 'Chandigarh', event_type: 'Marketing Workshop' },
    { company_name: 'Dabur India', rooms_count: 55, property_name: 'Radisson', city: 'Delhi', event_type: 'Ayurveda Conference' },
    { company_name: 'Titan Company', rooms_count: 90, property_name: 'The Park', city: 'Bangalore', event_type: 'Design Summit' },
    { company_name: 'Vedanta Limited', rooms_count: 145, property_name: 'Taj Krishna', city: 'Hyderabad', event_type: 'Mining Conference' },
    { company_name: 'UltraTech Cement', rooms_count: 120, property_name: 'ITC Rajputana', city: 'Jaipur', event_type: 'Construction Expo' },
    { company_name: 'Tata Steel', rooms_count: 200, property_name: 'Taj Bengal', city: 'Kolkata', event_type: 'Steel Summit' },
    { company_name: 'Power Grid Corp', rooms_count: 80, property_name: 'The Claridges', city: 'Delhi', event_type: 'Energy Forum' },
    { company_name: 'NTPC Limited', rooms_count: 130, property_name: 'Marriott', city: 'Noida', event_type: 'Power Conference' },
    { company_name: 'Grasim Industries', rooms_count: 75, property_name: 'Radisson Blu', city: 'Ahmedabad', event_type: 'Textile Meet' },
    { company_name: 'Hero MotoCorp', rooms_count: 155, property_name: 'The Oberoi', city: 'Delhi', event_type: 'Mobility Summit' },
    { company_name: 'Britannia Industries', rooms_count: 60, property_name: 'Taj Connemara', city: 'Chennai', event_type: 'Food Innovation' },
    { company_name: 'Havells India', rooms_count: 85, property_name: 'Crowne Plaza', city: 'Noida', event_type: 'Electrical Expo' },
    { company_name: 'Pidilite Industries', rooms_count: 70, property_name: 'Renaissance', city: 'Mumbai', event_type: 'Adhesive Summit' },
    { company_name: 'Marico Limited', rooms_count: 65, property_name: 'JW Marriott', city: 'Pune', event_type: 'FMCG Conference' },
    { company_name: 'Dr. Reddy\'s Labs', rooms_count: 110, property_name: 'Novotel', city: 'Hyderabad', event_type: 'Pharma Summit' },
    { company_name: 'Lupin Pharma', rooms_count: 95, property_name: 'The Lalit', city: 'Mumbai', event_type: 'Healthcare Meet' },
    { company_name: 'Shree Cement', rooms_count: 80, property_name: 'ITC Narmada', city: 'Ahmedabad', event_type: 'Infrastructure Forum' },
    { company_name: 'Berger Paints', rooms_count: 55, property_name: 'Taj City Centre', city: 'Kolkata', event_type: 'Color Trends' },
    { company_name: 'Torrent Pharma', rooms_count: 75, property_name: 'Courtyard Marriott', city: 'Ahmedabad', event_type: 'Drug Discovery' },
    { company_name: 'Zee Entertainment', rooms_count: 140, property_name: 'St. Regis', city: 'Mumbai', event_type: 'Media Summit' },
    { company_name: 'DLF Limited', rooms_count: 190, property_name: 'The Lodhi', city: 'Delhi', event_type: 'Real Estate Expo' },
    { company_name: 'Mindtree', rooms_count: 115, property_name: 'Conrad', city: 'Bangalore', event_type: 'Digital Summit' },
    { company_name: 'Mphasis', rooms_count: 100, property_name: 'Shangri-La', city: 'Bangalore', event_type: 'Cloud Conference' }
  ];

  // Gamification achievements
  const achievements = [
    { icon: Trophy, title: 'Top Rated Platform', desc: '4.9★ from 500+ corporates', color: 'text-gold' },
    { icon: Clock, title: 'Fast Response', desc: 'Avg 2hr hotel response', color: 'text-emerald-500' },
    { icon: Shield, title: '100% Secure', desc: 'Verified hotels only', color: 'text-blue-500' },
    { icon: CheckCircle2, title: 'Guaranteed Savings', desc: 'Best rates assured', color: 'text-purple-500' }
  ];

  // Indian Holidays for 2025-2026 (with names for tooltips)
  const indianHolidayNames = [
    // 2025 Holidays
    { date: new Date(2025, 0, 26), name: 'Republic Day' },
    { date: new Date(2025, 1, 26), name: 'Maha Shivaratri' },
    { date: new Date(2025, 2, 14), name: 'Holi' },
    { date: new Date(2025, 2, 31), name: 'Eid ul-Fitr' },
    { date: new Date(2025, 3, 6), name: 'Ram Navami' },
    { date: new Date(2025, 3, 10), name: 'Mahavir Jayanti' },
    { date: new Date(2025, 3, 14), name: 'Ambedkar Jayanti' },
    { date: new Date(2025, 3, 18), name: 'Good Friday' },
    { date: new Date(2025, 4, 1), name: 'May Day' },
    { date: new Date(2025, 4, 12), name: 'Buddha Purnima' },
    { date: new Date(2025, 5, 7), name: 'Eid ul-Adha' },
    { date: new Date(2025, 6, 6), name: 'Muharram' },
    { date: new Date(2025, 7, 15), name: 'Independence Day' },
    { date: new Date(2025, 7, 16), name: 'Janmashtami' },
    { date: new Date(2025, 8, 5), name: 'Milad un-Nabi' },
    { date: new Date(2025, 9, 2), name: 'Gandhi Jayanti / Dussehra' },
    { date: new Date(2025, 9, 20), name: 'Diwali' },
    { date: new Date(2025, 9, 21), name: 'Diwali (Day 2)' },
    { date: new Date(2025, 9, 23), name: 'Bhai Dooj' },
    { date: new Date(2025, 10, 5), name: 'Guru Nanak Jayanti' },
    { date: new Date(2025, 11, 25), name: 'Christmas' },
    // 2026 Holidays
    { date: new Date(2026, 0, 26), name: 'Republic Day' },
    { date: new Date(2026, 2, 4), name: 'Holi' },
    { date: new Date(2026, 2, 20), name: 'Eid ul-Fitr' },
    { date: new Date(2026, 3, 3), name: 'Good Friday' },
    { date: new Date(2026, 3, 14), name: 'Ambedkar Jayanti' },
    { date: new Date(2026, 4, 1), name: 'May Day / Buddha Purnima' },
    { date: new Date(2026, 7, 15), name: 'Independence Day' },
    { date: new Date(2026, 7, 25), name: 'Janmashtami' },
    { date: new Date(2026, 9, 2), name: 'Gandhi Jayanti' },
    { date: new Date(2026, 9, 9), name: 'Dussehra' },
    { date: new Date(2026, 10, 8), name: 'Diwali' },
    { date: new Date(2026, 10, 24), name: 'Guru Nanak Jayanti' },
    { date: new Date(2026, 11, 25), name: 'Christmas' }
  ];
  
  // Extract just dates for highlighting
  const indianHolidays = indianHolidayNames.map(h => h.date);

  // Client Testimonials - 15 testimonials
  const clientTestimonials = [
    {
      id: 1,
      user_name: 'Rajesh Sharma',
      user_designation: 'VP - Administration',
      user_company: 'Tata Consultancy Services',
      rating: 5,
      review: 'My Corporate Hub transformed how we manage our corporate events. Their platform helped us book 500+ room nights across 12 cities for our annual leadership summit. Exceptional service and significant cost savings!'
    },
    {
      id: 2,
      user_name: 'Priya Menon',
      user_designation: 'Head of Events',
      user_company: 'Infosys Limited',
      rating: 4.5,
      review: 'The speed and efficiency of My Corporate Hub is unmatched. We received competitive quotes from premium hotels within hours. Their team understood our requirements perfectly and delivered beyond expectations.'
    },
    {
      id: 3,
      user_name: 'Amit Patel',
      user_designation: 'Director - Corporate Services',
      user_company: 'Reliance Industries',
      rating: 5,
      review: 'We\'ve been using My Corporate Hub for all our MICE requirements. The platform is intuitive, the hotel options are excellent, and the pricing is always competitive. Highly recommended for large corporates.'
    },
    {
      id: 4,
      user_name: 'Sunita Reddy',
      user_designation: 'General Manager - HR',
      user_company: 'Wipro Technologies',
      rating: 4.5,
      review: 'From venue selection to final execution, My Corporate Hub made our annual tech conference seamless. The dedicated support team was available 24/7 and handled everything professionally.'
    },
    {
      id: 5,
      user_name: 'Vikram Singh',
      user_designation: 'Chief Administrative Officer',
      user_company: 'HDFC Bank',
      rating: 5,
      review: 'The transparency in pricing and the quality of hotels available on My Corporate Hub is remarkable. We saved over 25% on our quarterly training programs while getting better venues.'
    },
    {
      id: 6,
      user_name: 'Neha Kapoor',
      user_designation: 'Senior Manager - Events',
      user_company: 'Mahindra & Mahindra',
      rating: 4.5,
      review: 'My Corporate Hub\'s platform is a game-changer for corporate event planning. The real-time availability, instant quotes, and seamless booking process saved us countless hours.'
    },
    {
      id: 7,
      user_name: 'Arjun Nair',
      user_designation: 'VP - Operations',
      user_company: 'Larsen & Toubro',
      rating: 5,
      review: 'We organized our annual gala for 2000+ employees through My Corporate Hub. The team\'s attention to detail and vendor coordination was exceptional. Truly a one-stop solution.'
    },
    {
      id: 8,
      user_name: 'Kavitha Iyer',
      user_designation: 'Head of Procurement',
      user_company: 'Asian Paints',
      rating: 4.5,
      review: 'The variety of premium hotels and competitive pricing on My Corporate Hub has made them our go-to platform for all corporate stays and events. Outstanding service quality!'
    },
    {
      id: 9,
      user_name: 'Rahul Verma',
      user_designation: 'Director - Sales',
      user_company: 'Bajaj Finserv',
      rating: 5,
      review: 'Our sales meets are now easier to organize than ever. My Corporate Hub provides excellent options across tier-1 and tier-2 cities with consistent quality and service standards.'
    },
    {
      id: 10,
      user_name: 'Deepika Joshi',
      user_designation: 'AVP - Human Resources',
      user_company: 'Tech Mahindra',
      rating: 4.5,
      review: 'From hackathons to leadership retreats, My Corporate Hub has been our trusted partner. Their understanding of tech industry needs and quick turnaround time is impressive.'
    },
    {
      id: 11,
      user_name: 'Sanjay Gupta',
      user_designation: 'Chief People Officer',
      user_company: 'HCL Technologies',
      rating: 5,
      review: 'The platform\'s ease of use and the quality of partner hotels exceeded our expectations. My Corporate Hub has become an integral part of our employee engagement initiatives.'
    },
    {
      id: 12,
      user_name: 'Ananya Krishnan',
      user_designation: 'Manager - Corporate Affairs',
      user_company: 'Hindustan Unilever',
      rating: 4.5,
      review: 'My Corporate Hub\'s curated selection of 5-star properties and their negotiated rates have helped us elevate our brand events while staying within budget. Excellent platform!'
    },
    {
      id: 13,
      user_name: 'Manish Tiwari',
      user_designation: 'VP - Administration',
      user_company: 'Godrej Industries',
      rating: 5,
      review: 'The innovation day we organized through My Corporate Hub was flawless. From venue to catering to AV setup, everything was coordinated perfectly. Highly professional team!'
    },
    {
      id: 14,
      user_name: 'Pooja Mehta',
      user_designation: 'Head of Events & Communications',
      user_company: 'Adani Group',
      rating: 4.5,
      review: 'For our leadership retreat in Goa, My Corporate Hub delivered an exceptional experience. The attention to detail and personalized service made all the difference.'
    },
    {
      id: 15,
      user_name: 'Karthik Rajan',
      user_designation: 'Senior Director - Operations',
      user_company: 'Cipla Limited',
      rating: 5,
      review: 'My Corporate Hub understands the pharmaceutical industry\'s compliance requirements. Their curated hotel partners and professional approach make event planning stress-free.'
    }
  ];

  // Helper function to render stars including half stars
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const stars = [];
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="w-5 h-5 text-gold fill-gold" />);
    }
    
    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative w-5 h-5">
          <Star className="w-5 h-5 text-gold/30" />
          <div className="absolute inset-0 overflow-hidden w-1/2">
            <Star className="w-5 h-5 text-gold fill-gold" />
          </div>
        </div>
      );
    }
    
    return stars;
  };

  return (
    <div className="min-h-screen bg-white noise-texture overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-40 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/70 backdrop-blur-xl border-b border-gray-200/50 shadow-lg' 
            : 'bg-sky-100/90 backdrop-blur-md border-b border-sky-200 shadow-sm'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 relative z-50 flex-shrink-0">
              {/* Large Logo Extending Below Header */}
              <div className={`relative transition-all duration-500 ease-out`}>
                {/* Animated Glow Pulse - Only when not scrolled */}
                <div className={`absolute inset-0 rounded-full transition-all duration-500 ${isScrolled ? 'opacity-0 scale-100' : 'opacity-100 scale-110'}`}>
                  <div className="absolute inset-0 bg-gold/30 rounded-full blur-xl animate-pulse" />
                  <div className="absolute inset-0 bg-gold/20 rounded-full blur-2xl" style={{ animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite' }} />
                </div>
                {/* Logo Image - Large and extending below header */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                  alt="My Corporate Hub" 
                  className={`relative z-10 w-auto transition-all duration-500 drop-shadow-xl ${isScrolled ? 'h-16' : 'h-32 -mb-12'}`}
                />
              </div>
              {/* Show company name only when scrolled */}
              <span className={`font-heading text-navy font-bold tracking-tight transition-all duration-500 ${isScrolled ? 'text-2xl opacity-100' : 'text-2xl opacity-0 w-0 overflow-hidden'}`}>My Corporate Hub<sup className="text-xs ml-0.5">®</sup></span>
            </Link>
            
            {/* Navigation Menus - positioned to right of logo area */}
            <div className="hidden md:flex items-center gap-6 flex-1 justify-center ml-8">
              <Link to="/services" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Services</Link>
              <Link to="/properties" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Properties</Link>
              <Link to="/gifts" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Gifting</Link>
              <Link to="/event-permissions" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Event Permits</Link>
              <Link to="/trainers" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Trainers</Link>
              <Link to="/pricing" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Pricing</Link>
            </div>

            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button 
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-navy" />
                ) : (
                  <Menu className="w-6 h-6 text-navy" />
                )}
              </button>
              
              {user ? (
                <>
                  <Link to={`/dashboard/${user.role}`} className="hidden sm:block">
                    <Button variant="ghost" className="text-navy font-medium">Dashboard</Button>
                  </Link>
                  <Button onClick={logout} variant="outline" className="border-navy text-navy hidden sm:flex">Logout</Button>
                </>
              ) : (
                <>
                  <Button onClick={() => setShowLoginDialog(true)} variant="ghost" className="text-navy font-medium hidden sm:flex" data-testid="nav-login-button">
                    Sign In
                  </Button>
                  <Button className="bg-gold hover:bg-gold-light text-navy font-bold px-4 sm:px-6 shadow-md text-sm sm:text-base" onClick={() => setShowLoginDialog(true)}>
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-2 animate-in slide-in-from-top-2 duration-200">
              <Link 
                to="/services" 
                className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Services
              </Link>
              <Link 
                to="/properties" 
                className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Properties
              </Link>
              <Link 
                to="/gifts" 
                className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Gifting
              </Link>
              <Link 
                to="/event-permissions" 
                className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Event Permits
              </Link>
              <Link 
                to="/trainers" 
                className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Trainers
              </Link>
              <Link 
                to="/pricing" 
                className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              
              {/* Mobile Login/Dashboard Section */}
              <div className="pt-3 border-t border-gray-200 mt-3 space-y-2">
                {user ? (
                  <>
                    <Link 
                      to={`/dashboard/${user.role}`}
                      className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                        Dashboard
                      </span>
                    </Link>
                    <button 
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="block w-full text-left py-3 px-4 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                      </span>
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => { setShowLoginDialog(true); setMobileMenuOpen(false); }}
                    className="block w-full text-left py-3 px-4 bg-navy text-white hover:bg-navy-light rounded-lg font-medium transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      Sign In
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section with Background Image */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&q=80&w=2000')`
          }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/80 via-navy-900/70 to-navy-900/90"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 w-full">
          <div className="text-center max-w-5xl mx-auto">
            <div className="inline-block mb-4 sm:mb-6">
              <span className="bg-gold/20 text-gold border border-gold/40 px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider sm:tracking-widest backdrop-blur-sm">
                <span className="hidden sm:inline">India's Premier Corporate Events Platform</span>
                <span className="sm:hidden">Corporate Events Platform</span>
              </span>
            </div>
            
            <h1 className="font-heading text-3xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.1] mb-4 sm:mb-6">
              <span className="block">Seamlessly plan</span>
              <span className="block mt-2"><span className="text-gold">Conferences</span> & <span className="text-gold">Offsites</span></span>
            </h1>
            
            <p className="text-slate-200 text-base sm:text-lg md:text-xl leading-relaxed mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
              Access 150+ premium 5-star hotels across India for your corporate events, incentive trips, and team retreats.
            </p>
            
            {/* Advanced MICE Search Bar - Moved Up */}
            <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-4 sm:p-6 max-w-5xl mx-auto mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-navy font-semibold">
                  <Star className="w-5 h-5 text-gold" />
                  <span>Start your RFP</span>
                </div>
                <span className="text-xs text-slate-500 hidden sm:block">Get quotes in 24 hours</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="relative">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    <MapPin className="inline w-3 h-3 mr-1" />
                    Location
                  </label>
                  <Select value={searchLocation} onValueChange={setSearchLocation}>
                    <SelectTrigger className="w-full h-12 text-base border-gray-200">
                      <SelectValue placeholder="Select City" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mumbai">Mumbai</SelectItem>
                      <SelectItem value="delhi">Delhi NCR</SelectItem>
                      <SelectItem value="bangalore">Bangalore</SelectItem>
                      <SelectItem value="hyderabad">Hyderabad</SelectItem>
                      <SelectItem value="chennai">Chennai</SelectItem>
                      <SelectItem value="pune">Pune</SelectItem>
                      <SelectItem value="goa">Goa</SelectItem>
                      <SelectItem value="jaipur">Jaipur</SelectItem>
                      <SelectItem value="udaipur">Udaipur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="relative">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    <Building2 className="inline w-3 h-3 mr-1" />
                    Event Type
                  </label>
                  <Select value={searchEventType} onValueChange={setSearchEventType}>
                    <SelectTrigger className="w-full h-12 text-base border-gray-200">
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conference">Conference</SelectItem>
                      <SelectItem value="offsite">Offsite</SelectItem>
                      <SelectItem value="dealer-meet">Dealer Meet</SelectItem>
                      <SelectItem value="product-launch">Product Launch</SelectItem>
                      <SelectItem value="agm">Annual General Meeting</SelectItem>
                      <SelectItem value="team-building">Team Building</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Simplified Row - Check-In/Check-Out and Attendees */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
                <div className="relative">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    <Calendar className="inline w-3 h-3 mr-1" />
                    Check-In
                  </label>
                  <DatePicker
                    selected={searchCheckIn}
                    onChange={(date) => setSearchCheckIn(date)}
                    onMonthChange={(date) => setCheckInViewedMonth(date)}
                    selectsStart
                    startDate={searchCheckIn}
                    endDate={searchCheckOut}
                    minDate={new Date()}
                    dateFormat="dd MMM yyyy"
                    placeholderText="Check-in date"
                    className="w-full h-12 px-3 text-base text-navy border border-gray-200 rounded-lg focus:border-gold focus:outline-none cursor-pointer bg-white"
                    wrapperClassName="w-full"
                    highlightDates={indianHolidays}
                    dayClassName={(date) => {
                      const isHoliday = indianHolidays.some(h => 
                        h.getDate() === date.getDate() && 
                        h.getMonth() === date.getMonth() && 
                        h.getFullYear() === date.getFullYear()
                      );
                      return isHoliday ? "holiday-highlight" : undefined;
                    }}
                    renderDayContents={(day, date) => {
                      const holiday = indianHolidayNames.find(h => 
                        h.date.getDate() === date.getDate() && 
                        h.date.getMonth() === date.getMonth() && 
                        h.date.getFullYear() === date.getFullYear()
                      );
                      return (
                        <div className="relative h-full w-full flex flex-col items-center justify-center" title={holiday?.name || ''}>
                          <span>{day}</span>
                          {holiday && <span className="w-1.5 h-1.5 rounded-full mt-0.5" style={{backgroundColor: '#d4af37'}}></span>}
                        </div>
                      );
                    }}
                  >
                    <div className="px-4 py-3 bg-amber-50 border-t border-gold/30">
                      <p className="text-xs font-bold text-navy mb-2 flex items-center gap-1">
                        <span style={{color: '#d4af37'}}>●</span> Holidays in {checkInViewedMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                      </p>
                      <div className="text-xs text-slate-700 space-y-1.5 max-h-24 overflow-y-auto pr-2 scrollbar-thin">
                        {indianHolidayNames
                          .filter(h => 
                            h.date.getMonth() === checkInViewedMonth.getMonth() && 
                            h.date.getFullYear() === checkInViewedMonth.getFullYear()
                          )
                          .length > 0 ? (
                            indianHolidayNames
                              .filter(h => 
                                h.date.getMonth() === checkInViewedMonth.getMonth() && 
                                h.date.getFullYear() === checkInViewedMonth.getFullYear()
                              )
                              .map((h, i) => (
                                <div key={i} className="flex items-center gap-2 py-0.5">
                                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor: '#d4af37'}}></span>
                                  <span className="font-medium text-navy">{h.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                                  <span className="text-slate-400">-</span>
                                  <span className="text-slate-700">{h.name}</span>
                                </div>
                              ))
                          ) : (
                            <p className="text-slate-400 italic">No holidays this month</p>
                          )}
                      </div>
                    </div>
                  </DatePicker>
                </div>
                
                <div className="relative">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    <Calendar className="inline w-3 h-3 mr-1" />
                    Check-Out
                  </label>
                  <DatePicker
                    selected={searchCheckOut}
                    onChange={(date) => setSearchCheckOut(date)}
                    onMonthChange={(date) => setCheckOutViewedMonth(date)}
                    selectsEnd
                    startDate={searchCheckIn}
                    endDate={searchCheckOut}
                    minDate={searchCheckIn || new Date()}
                    dateFormat="dd MMM yyyy"
                    placeholderText="Check-out date"
                    className="w-full h-12 px-3 text-base text-navy border border-gray-200 rounded-lg focus:border-gold focus:outline-none cursor-pointer bg-white"
                    wrapperClassName="w-full"
                    highlightDates={indianHolidays}
                    dayClassName={(date) => {
                      const isHoliday = indianHolidays.some(h => 
                        h.getDate() === date.getDate() && 
                        h.getMonth() === date.getMonth() && 
                        h.getFullYear() === date.getFullYear()
                      );
                      return isHoliday ? "holiday-highlight" : undefined;
                    }}
                    renderDayContents={(day, date) => {
                      const holiday = indianHolidayNames.find(h => 
                        h.date.getDate() === date.getDate() && 
                        h.date.getMonth() === date.getMonth() && 
                        h.date.getFullYear() === date.getFullYear()
                      );
                      return (
                        <div className="relative h-full w-full flex flex-col items-center justify-center" title={holiday?.name || ''}>
                          <span>{day}</span>
                          {holiday && <span className="w-1.5 h-1.5 rounded-full mt-0.5" style={{backgroundColor: '#d4af37'}}></span>}
                        </div>
                      );
                    }}
                  >
                    <div className="px-4 py-3 bg-amber-50 border-t border-gold/30">
                      <p className="text-xs font-bold text-navy mb-2 flex items-center gap-1">
                        <span style={{color: '#d4af37'}}>●</span> Holidays in {checkOutViewedMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                      </p>
                      <div className="text-xs text-slate-700 space-y-1.5 max-h-24 overflow-y-auto pr-2 scrollbar-thin">
                        {indianHolidayNames
                          .filter(h => 
                            h.date.getMonth() === checkOutViewedMonth.getMonth() && 
                            h.date.getFullYear() === checkOutViewedMonth.getFullYear()
                          )
                          .length > 0 ? (
                            indianHolidayNames
                              .filter(h => 
                                h.date.getMonth() === checkOutViewedMonth.getMonth() && 
                                h.date.getFullYear() === checkOutViewedMonth.getFullYear()
                              )
                              .map((h, i) => (
                                <div key={i} className="flex items-center gap-2 py-0.5">
                                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor: '#d4af37'}}></span>
                                  <span className="font-medium text-navy">{h.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                                  <span className="text-slate-400">-</span>
                                  <span className="text-slate-700">{h.name}</span>
                                </div>
                              ))
                          ) : (
                            <p className="text-slate-400 italic">No holidays this month</p>
                          )}
                      </div>
                    </div>
                  </DatePicker>
                </div>
                
                <div className="relative">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    <Users className="inline w-3 h-3 mr-1" />
                    Attendees
                  </label>
                  <Input 
                    type="number" 
                    placeholder="50" 
                    value={searchAttendees}
                    onChange={(e) => setSearchAttendees(e.target.value)}
                    className="w-full h-12 text-base border-gray-200"
                    min="1"
                  />
                </div>
                
                <div className="col-span-2 md:col-span-2">
                  <Button 
                    onClick={handleGetInstantQuote}
                    className="w-full bg-navy hover:bg-navy-light text-white font-bold text-base h-12 rounded-lg shadow-lg"
                  >
                    Get Instant Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>

            {/* CTA Buttons - Below Search */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/properties">
                <Button variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 font-semibold px-6 py-3 text-base rounded-lg backdrop-blur-sm">
                  Browse All Venues
                </Button>
              </Link>
              <a href={`https://wa.me/919664870766?text=${encodeURIComponent("Hi! I need help with corporate event booking.")}`} target="_blank" rel="noopener noreferrer">
                <Button className="bg-gold hover:bg-gold-light text-navy font-bold px-6 py-3 text-base rounded-lg shadow-lg">
                  Talk to an Expert
                </Button>
              </a>
            </div>
          </div>
          
          {/* Stats Bar */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">150+</div>
              <div className="text-slate-300 text-sm">Verified Hotels</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">45+</div>
              <div className="text-slate-300 text-sm">Cities Covered</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">500+</div>
              <div className="text-slate-300 text-sm">Corporate Clients</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">10k+</div>
              <div className="text-slate-300 text-sm">Successful Events</div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section - Infinite Scroll Marquee */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-gray-50 border-b overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-navy-900 font-heading text-2xl sm:text-3xl font-bold mb-4">
            TRUSTED BY INDIA'S LEADING CORPORATIONS
          </h2>
          <p className="text-center text-slate-800 mb-8 sm:mb-12 text-sm sm:text-base">
            Join 500+ companies who trust us for their corporate events
          </p>
        </div>
        
        {/* Marquee Container */}
        <div className="relative">
          {/* Gradient Overlays for Smooth Edges */}
          <div className="absolute left-0 top-0 bottom-0 w-20 sm:w-40 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-20 sm:w-40 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
          
          {/* Scrolling Track */}
          <div className="flex animate-marquee hover:pause-animation">
            {/* First Set of Logos */}
            {trustedLogos.map((company, idx) => (
              <div 
                key={`first-${idx}`} 
                className="flex-shrink-0 mx-4 sm:mx-6 group"
              >
                <div className="flex flex-col items-center justify-center w-32 sm:w-44 h-28 sm:h-36 p-3 sm:p-4 bg-white border border-slate-200 rounded-xl hover:border-gold hover:shadow-xl transition-all duration-300">
                  <img 
                    src={company.logo} 
                    alt={company.name}
                    className="w-full h-14 sm:h-20 object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                    style={{ maxWidth: '140px', objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-slate-700 font-semibold text-xs text-center">${company.name}</span>`;
                    }}
                  />
                  <span className="mt-2 text-xs font-medium text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center truncate w-full">
                    {company.name}
                  </span>
                </div>
              </div>
            ))}
            {/* Duplicate Set for Seamless Loop */}
            {trustedLogos.map((company, idx) => (
              <div 
                key={`second-${idx}`} 
                className="flex-shrink-0 mx-4 sm:mx-6 group"
              >
                <div className="flex flex-col items-center justify-center w-32 sm:w-44 h-28 sm:h-36 p-3 sm:p-4 bg-white border border-slate-200 rounded-xl hover:border-gold hover:shadow-xl transition-all duration-300">
                  <img 
                    src={company.logo} 
                    alt={company.name}
                    className="w-full h-14 sm:h-20 object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                    style={{ maxWidth: '140px', objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-slate-700 font-semibold text-xs text-center">${company.name}</span>`;
                    }}
                  />
                  <span className="mt-2 text-xs font-medium text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center truncate w-full">
                    {company.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gamification Achievements */}
      <section className="py-12 bg-gradient-to-r from-navy-800 to-navy-900 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {achievements.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -8,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                transition={{ 
                  delay: idx * 0.1,
                  duration: 0.5,
                  ease: "easeOut"
                }}
                className="text-center p-6 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/25 hover:border-white/40 hover:shadow-2xl transition-all duration-300 cursor-pointer group"
              >
                <motion.div
                  whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.2 }}
                  transition={{ duration: 0.5 }}
                >
                  <item.icon className={`w-10 h-10 mx-auto mb-3 ${item.color} group-hover:drop-shadow-lg transition-all duration-300`} />
                </motion.div>
                <h3 className="text-white font-bold mb-1 group-hover:text-gold transition-colors duration-300">{item.title}</h3>
                <p className="text-slate-200 text-sm transition-colors duration-300">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hotel Partners Section - Featured Grid with Stats */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <span className="inline-block bg-gold/10 text-gold border border-gold/30 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
              Trusted Partners
            </span>
            <h2 className="text-navy font-heading text-3xl sm:text-4xl font-bold mb-4">
              Our Premium Hotel Partners
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-base sm:text-lg">
              Partnered with India's finest 5-star hotel chains to deliver exceptional venues for your corporate events
            </p>
          </div>

          {/* Stats Summary */}
          <div className="flex flex-wrap justify-center gap-8 mb-12">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-navy">500+</div>
              <div className="text-slate-600 text-sm">Total Venues</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-navy">25+</div>
              <div className="text-slate-600 text-sm">Cities Covered</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-navy">8</div>
              <div className="text-slate-600 text-sm">Hotel Chains</div>
            </div>
          </div>

          {/* Partners Grid - Organized by Tier */}
          <div className="space-y-10">
            {/* Platinum Partners */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide bg-gradient-to-r from-slate-700 to-slate-900 text-white">
                  Platinum Partners
                </span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {hotelPartners.filter(h => h.tier === 'Platinum').map((hotel, idx) => (
                  <div 
                    key={idx}
                    className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border border-slate-100"
                  >
                    {/* Partnership Tier Badge */}
                    <div className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-gradient-to-r from-slate-700 to-slate-900 text-white">
                      {hotel.tier}
                    </div>

                    {/* Hotel Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={hotel.logo} 
                        alt={hotel.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent"></div>
                      
                      {/* Hotel Name Overlay */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-white font-bold text-lg drop-shadow-lg">{hotel.name}</h3>
                        <p className="text-white/80 text-sm">{hotel.description}</p>
                      </div>
                    </div>

                    {/* Stats Bar */}
                    <div className="p-4 bg-slate-50">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <div className="text-xl font-bold text-navy">{hotel.venues}+</div>
                            <div className="text-xs text-slate-500">Venues</div>
                          </div>
                          <div className="w-px h-8 bg-slate-200"></div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-navy">{hotel.cities}</div>
                            <div className="text-xs text-slate-500">Cities</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-gold">
                          <Star className="w-4 h-4 fill-gold" />
                          <span className="font-semibold text-sm">5.0</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gold Partners */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide bg-gradient-to-r from-gold to-amber-500 text-navy">
                  Gold Partners
                </span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {hotelPartners.filter(h => h.tier === 'Gold').map((hotel, idx) => (
                  <div 
                    key={idx}
                    className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border border-slate-100"
                  >
                    {/* Partnership Tier Badge */}
                    <div className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-gradient-to-r from-gold to-amber-500 text-navy">
                      {hotel.tier}
                    </div>

                    {/* Hotel Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={hotel.logo} 
                        alt={hotel.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent"></div>
                      
                      {/* Hotel Name Overlay */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-white font-bold text-lg drop-shadow-lg">{hotel.name}</h3>
                        <p className="text-white/80 text-sm">{hotel.description}</p>
                      </div>
                    </div>

                    {/* Stats Bar */}
                    <div className="p-4 bg-slate-50">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <div className="text-xl font-bold text-navy">{hotel.venues}+</div>
                            <div className="text-xs text-slate-500">Venues</div>
                          </div>
                          <div className="w-px h-8 bg-slate-200"></div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-navy">{hotel.cities}</div>
                            <div className="text-xs text-slate-500">Cities</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-gold">
                          <Star className="w-4 h-4 fill-gold" />
                          <span className="font-semibold text-sm">5.0</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Silver Partners */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide bg-gradient-to-r from-slate-300 to-slate-400 text-slate-700">
                  Silver Partners
                </span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {hotelPartners.filter(h => h.tier === 'Silver').map((hotel, idx) => (
                  <div 
                    key={idx}
                    className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 border border-slate-100"
                  >
                    {/* Partnership Tier Badge */}
                    <div className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-gradient-to-r from-slate-300 to-slate-400 text-slate-700">
                      {hotel.tier}
                    </div>

                    {/* Hotel Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={hotel.logo} 
                        alt={hotel.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent"></div>
                      
                      {/* Hotel Name Overlay */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-white font-bold text-lg drop-shadow-lg">{hotel.name}</h3>
                        <p className="text-white/80 text-sm">{hotel.description}</p>
                      </div>
                    </div>

                    {/* Stats Bar */}
                    <div className="p-4 bg-slate-50">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <div className="text-xl font-bold text-navy">{hotel.venues}+</div>
                            <div className="text-xs text-slate-500">Venues</div>
                          </div>
                          <div className="w-px h-8 bg-slate-200"></div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-navy">{hotel.cities}</div>
                            <div className="text-xs text-slate-500">Cities</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-gold">
                          <Star className="w-4 h-4 fill-gold" />
                          <span className="font-semibold text-sm">5.0</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <Link to="/properties">
              <Button className="bg-navy hover:bg-navy-800 text-white font-bold px-10 py-4 text-base shadow-lg rounded-full">
                Explore All Partner Venues <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Live Booking Feed */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="gold-line mb-6"></div>
              <h2 className="font-heading text-4xl md:text-5xl text-slate-900 mb-6">Real-Time Bookings</h2>
              <p className="text-slate-700 text-lg leading-relaxed mb-8">
                Join hundreds of corporates who trust My Corporate Hub for their event needs. Watch live as companies book their next big events.
              </p>
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2 text-emerald-600">
                  <span className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></span>
                  <span className="font-semibold">Live Feed</span>
                </div>
                <span className="text-slate-800">Updated in real-time</span>
              </div>
              <Link to="/bulk-rfp/new">
                <Button className="bg-gold hover:bg-gold-light text-navy-900 font-bold px-8 py-4 text-base shadow-lg">
                  Get Started Now <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="bg-navy rounded-2xl p-6 ticker-container relative overflow-hidden shadow-2xl">
              <div className="ticker-content space-y-4">
                {/* First set of bookings */}
                {liveBookings.map((booking, idx) => (
                  <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/15 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-semibold">{booking.company_name}</p>
                        <p className="text-gold text-sm">{booking.rooms_count} rooms at {booking.property_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <MapPin className="w-3 h-3 text-white/60" />
                      <span className="text-white/60 text-xs">{booking.city}</span>
                      <span className="text-white/40">•</span>
                      <span className="text-white/60 text-xs">{booking.event_type}</span>
                    </div>
                  </div>
                ))}
                {/* Duplicate for seamless loop */}
                {liveBookings.map((booking, idx) => (
                  <div key={`dup-${idx}`} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/15 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-semibold">{booking.company_name}</p>
                        <p className="text-gold text-sm">{booking.rooms_count} rooms at {booking.property_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <MapPin className="w-3 h-3 text-white/60" />
                      <span className="text-white/60 text-xs">{booking.city}</span>
                      <span className="text-white/40">•</span>
                      <span className="text-white/60 text-xs">{booking.event_type}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="gold-line mx-auto mb-6"></div>
            <h2 className="font-heading text-4xl md:text-5xl text-navy mb-4">Featured Properties</h2>
            <p className="text-slate-800 text-lg max-w-2xl mx-auto">
              Handpicked 5-star venues perfect for your next corporate event
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(featuredProperties.length > 0 ? featuredProperties : [
              { id: 1, name: 'Taj Palace Delhi', city: 'Delhi', price_per_day: 250000, rating: 4.9, images: ['https://images.pexels.com/photos/2123430/pexels-photo-2123430.jpeg'] },
              { id: 2, name: 'ITC Grand Chola', city: 'Chennai', price_per_day: 300000, rating: 4.8, images: ['https://images.pexels.com/photos/1134176/pexels-photo-1134176.jpeg'] },
              { id: 3, name: 'The Oberoi Udaivilas', city: 'Udaipur', price_per_day: 400000, rating: 5.0, images: ['https://images.pexels.com/photos/2417842/pexels-photo-2417842.jpeg'] },
              { id: 4, name: 'JW Marriott Mumbai', city: 'Mumbai', price_per_day: 200000, rating: 4.7, images: ['https://images.pexels.com/photos/1579253/pexels-photo-1579253.jpeg'] },
            ]).map((property) => (
              <Link key={property.id} to={`/properties/${property.id}`}>
                <motion.div
                  whileHover={{ y: -8 }}
                  className="card-premium cursor-pointer"
                >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={property.images?.[0] || 'https://images.pexels.com/photos/1134176/pexels-photo-1134176.jpeg'} 
                    alt={property.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-gold fill-gold" />
                      <span className="text-navy font-semibold text-sm">{property.rating}</span>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg text-navy font-semibold mb-1">{property.name}</h3>
                  <div className="flex items-center gap-1 text-slate-700 text-sm mb-3">
                    <MapPin className="w-4 h-4" />
                    <span>{property.city}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-gold font-semibold">
                      ₹{(property.price_per_day / 1000).toFixed(0)}K<span className="text-slate-800 font-normal text-sm">/day</span>
                    </p>
                    <Button size="sm" className="bg-navy hover:bg-gold text-white hover:text-navy font-semibold transition-all">
                      View →
                    </Button>
                  </div>
                </div>
              </motion.div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/properties">
              <Button className="bg-gold hover:bg-gold-light text-navy-900 font-bold text-base px-10 py-4 shadow-lg">
                View All Properties <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-navy">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <div className="gold-line mx-auto mb-6"></div>
            <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">Why Choose My Corporate Hub</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Clock, title: '1-Hour Response', desc: 'Get competitive quotes from multiple hotels within an hour of your RFP submission.' },
              { icon: Shield, title: 'Verified Partners', desc: 'All our hotel partners are verified 5-star properties with proven track records.' },
              { icon: Trophy, title: 'Best Price Guarantee', desc: 'Transparent bidding ensures you always get the most competitive rates.' },
              { icon: Hotel, title: 'End-to-End Support', desc: 'From planning to execution, our team supports you throughout the journey.' },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gold/20 rounded-lg flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-gold" />
                </div>
                <h3 className="font-heading text-xl text-white mb-3">{item.title}</h3>
                <p className="text-slate-300">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - Interactive Carousel */}
      <section className="py-20 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block bg-gold/10 text-gold border border-gold/30 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
              Testimonials
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-navy mb-4">What Our Clients Say</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Trusted by India's leading corporations for their corporate events and MICE requirements
            </p>
          </div>

          {/* Featured Testimonial - Large Card */}
          <div className="mb-12 relative">
            {/* Play/Pause Button */}
            <button
              onClick={() => setIsTestimonialPaused(!isTestimonialPaused)}
              className={`absolute -top-2 right-0 z-10 flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 shadow-lg ${
                isTestimonialPaused 
                  ? 'bg-green-500 hover:bg-green-600 text-white' 
                  : 'bg-white hover:bg-slate-100 text-navy border border-slate-200'
              }`}
            >
              {isTestimonialPaused ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  Play
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                  Let me Read
                </>
              )}
            </button>
            
            <motion.div
              key={activeTestimonialIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-gradient-to-br from-navy via-navy-800 to-navy-900 rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-2xl"
            >
              {/* Background Decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gold/5 rounded-full blur-2xl" />
              
              <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center">
                {/* Left - Avatar & Info */}
                <div className="flex-shrink-0 text-center md:text-left">
                  <div className="w-24 h-24 bg-gradient-to-br from-gold to-yellow-400 rounded-full flex items-center justify-center mx-auto md:mx-0 mb-4 shadow-lg">
                    <span className="text-navy font-bold text-3xl">
                      {clientTestimonials[activeTestimonialIndex]?.user_name?.charAt(0)}
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-xl">{clientTestimonials[activeTestimonialIndex]?.user_name}</h3>
                  <p className="text-slate-300 text-sm">{clientTestimonials[activeTestimonialIndex]?.user_designation}</p>
                  <p className="text-gold font-medium">{clientTestimonials[activeTestimonialIndex]?.user_company}</p>
                  <div className="flex items-center justify-center md:justify-start gap-1 mt-3">
                    {renderStars(clientTestimonials[activeTestimonialIndex]?.rating || 5)}
                  </div>
                </div>
                
                {/* Right - Quote */}
                <div className="flex-1">
                  <Quote className="w-12 h-12 text-gold/30 mb-4" />
                  <p className="text-white/90 text-lg md:text-xl leading-relaxed italic">
                    "{clientTestimonials[activeTestimonialIndex]?.review}"
                  </p>
                </div>
              </div>
            </motion.div>
            
            {/* Progress Bar - shows auto-play timing */}
            <div className="mt-4 h-1 bg-slate-200 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-gold"
                initial={{ width: "0%" }}
                animate={{ width: isTestimonialPaused ? "0%" : "100%" }}
                transition={{ 
                  duration: isTestimonialPaused ? 0 : 5, 
                  ease: "linear",
                  repeat: isTestimonialPaused ? 0 : Infinity
                }}
                key={`progress-${activeTestimonialIndex}-${isTestimonialPaused}`}
              />
            </div>
          </div>

          {/* Navigation Dots & Arrows */}
          <div className="flex items-center justify-center gap-6 mb-12">
            <button 
              onClick={() => setActiveTestimonialIndex(prev => prev === 0 ? clientTestimonials.length - 1 : prev - 1)}
              className="w-12 h-12 rounded-full bg-navy/10 hover:bg-navy hover:text-white text-navy flex items-center justify-center transition-all duration-300 shadow-md"
            >
              <ChevronRight className="w-6 h-6 rotate-180" />
            </button>
            
            <div className="flex items-center gap-2">
              {clientTestimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonialIndex(index)}
                  className={`transition-all duration-300 rounded-full ${
                    activeTestimonialIndex === index 
                      ? 'w-8 h-3 bg-gold' 
                      : 'w-3 h-3 bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>
            
            <button 
              onClick={() => setActiveTestimonialIndex(prev => prev === clientTestimonials.length - 1 ? 0 : prev + 1)}
              className="w-12 h-12 rounded-full bg-navy/10 hover:bg-navy hover:text-white text-navy flex items-center justify-center transition-all duration-300 shadow-md"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Mini Testimonial Cards - Preview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {clientTestimonials.slice(0, 4).map((testimonial, index) => (
              <button
                key={testimonial.id}
                onClick={() => setActiveTestimonialIndex(index)}
                className={`p-4 rounded-xl text-left transition-all duration-300 ${
                  activeTestimonialIndex === index 
                    ? 'bg-gold/20 border-2 border-gold shadow-lg scale-105' 
                    : 'bg-white border border-slate-200 hover:border-gold/50 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                    activeTestimonialIndex === index ? 'bg-gold text-navy' : 'bg-navy text-white'
                  }`}>
                    {testimonial.user_name.charAt(0)}
                  </div>
                  <div className="overflow-hidden">
                    <p className="font-semibold text-navy text-sm truncate">{testimonial.user_name}</p>
                    <p className="text-xs text-slate-500 truncate">{testimonial.user_company}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-600 line-clamp-2">"{testimonial.review.substring(0, 60)}..."</p>
              </button>
            ))}
          </div>

          {/* View All Button */}
          <div className="text-center">
            <Button
              onClick={() => setShowAllTestimonials(true)}
              className="bg-navy hover:bg-navy-800 text-white px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Quote className="w-5 h-5 mr-2" />
              View All {clientTestimonials.length} Testimonials
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* All Testimonials Modal */}
      <Dialog open={showAllTestimonials} onOpenChange={setShowAllTestimonials}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden p-0">
          <div className="bg-gradient-to-r from-navy to-navy-800 p-6 sticky top-0 z-10">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-white text-center">
                💬 All Client Testimonials
              </DialogTitle>
              <p className="text-slate-300 text-center mt-2">
                {clientTestimonials.length} reviews from our valued clients
              </p>
            </DialogHeader>
          </div>
          
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <div className="grid md:grid-cols-2 gap-6">
              {clientTestimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border border-slate-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-gold to-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-navy font-bold">{testimonial.user_name.charAt(0)}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-navy">{testimonial.user_name}</p>
                          <p className="text-xs text-slate-500">{testimonial.user_designation}</p>
                          <p className="text-xs text-gold font-medium">{testimonial.user_company}</p>
                        </div>
                        <div className="flex items-center gap-0.5">
                          {renderStars(testimonial.rating)}
                        </div>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed">"{testimonial.review}"</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          <div className="bg-slate-50 p-4 border-t sticky bottom-0">
            <Button 
              onClick={() => setShowAllTestimonials(false)}
              className="w-full bg-gold hover:bg-gold-light text-navy font-bold py-3"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="gold-line mx-auto mb-6"></div>
          <h2 className="font-heading text-4xl md:text-5xl text-navy mb-6">Ready to Plan Your Next Event?</h2>
          <p className="text-slate-800 text-lg mb-8">
            Submit your requirements and receive competitive quotes from premium hotels within an hour.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/bulk-rfp/new">
              <Button className="bg-navy hover:bg-navy-700 text-white font-bold text-base px-10 py-4 shadow-lg">
                Submit RFP Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button className="bg-gold hover:bg-gold-light text-navy-900 font-bold text-base px-10 py-4 shadow-lg" onClick={() => setShowChat(true)}>
              <MessageCircle className="mr-2 w-5 h-5" /> Chat with Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Newsletter Section */}
          <div className="bg-gradient-to-r from-gold/20 to-gold/10 border-2 border-gold/50 rounded-xl p-8 mb-12 shadow-lg">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-white mb-2">🔔 Stay Updated with Event Insights</h3>
              <p className="text-slate-200 mb-6">Get exclusive event planning tips, venue updates, and special offers</p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const email = e.target.email.value;
                try {
                  await api.subscribeNewsletter({ email });
                  toast.success('Successfully subscribed to newsletter!');
                  e.target.reset();
                } catch (error) {
                  toast.error('Failed to subscribe. Please try again.');
                }
              }} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  required
                  className="flex-1 bg-white border-gold/30 text-navy placeholder:text-slate-700 h-12"
                />
                <Button type="submit" className="bg-gold hover:bg-gold-light text-navy font-bold whitespace-nowrap px-8 h-12 shadow-md">
                  Subscribe Now
                </Button>
              </form>
            </div>
          </div>

          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                  alt="My Corporate Hub" 
                  className="h-12 w-auto"
                />
                <span className="font-heading text-2xl text-white font-semibold">My Corporate Hub</span>
              </div>
              <p className="text-slate-300">India's premier platform for corporate events, conferences, and luxury hospitality.</p>
            </div>
            <div>
              <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/services" className="text-slate-300 hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/properties" className="text-slate-300 hover:text-white transition-colors">Properties</Link></li>
                <li><Link to="/gifts" className="text-slate-300 hover:text-white transition-colors">Corporate Gifting</Link></li>
                <li><Link to="/event-permissions" className="text-slate-300 hover:text-white transition-colors">Event Permits</Link></li>
                <li><Link to="/careers" className="text-slate-300 hover:text-white transition-colors">Careers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Legal</h4>
              <ul className="space-y-3">
                <li><Link to="/privacy-policy" className="text-slate-300 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-conditions" className="text-slate-300 hover:text-white transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/refund-policy" className="text-slate-300 hover:text-white transition-colors">Refund Policy</Link></li>
                <li><Link to="/cancellation-policy" className="text-slate-300 hover:text-white transition-colors">Cancellation Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Careers</h4>
              <ul className="space-y-3">
                <li><Link to="/careers" className="text-slate-300 hover:text-white transition-colors">Open Positions</Link></li>
                <li><Link to="/careers#benefits" className="text-slate-300 hover:text-white transition-colors">Why Join Us</Link></li>
                <li><Link to="/careers#culture" className="text-slate-300 hover:text-white transition-colors">Our Culture</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-gold font-semibold uppercase tracking-wide mb-4">Contact Us</h4>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-start">
                  <span className="mr-2">📞</span>
                  <span>+91 96648 70766</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✉️</span>
                  <span>info@mycorporatehub.com</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">💬</span>
                  <a href={`https://wa.me/919664870766?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">
                    +91 96648 70766
                  </a>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">📍</span>
                  <span>Corporate Office:<br/>L23, Express Zone, A Wing,<br/>Western Express Highway,<br/>Panch Bawadi, Malad East,<br/>Mumbai 400063, Maharashtra, India</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🏢</span>
                  <span>GST: 27AAMCB6393G1Z9</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Payment Partner - Razorpay */}
          <div className="border-t border-navy-700 mt-8 pt-6">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              <span className="text-white text-sm font-medium">Secure Payments Powered By</span>
              <div className="flex items-center gap-4 bg-white px-6 py-3 rounded-lg shadow-lg">
                <img 
                  src="https://cdn.razorpay.com/logo.svg" 
                  alt="Razorpay" 
                  className="h-6"
                />
                <div className="w-px h-8 bg-slate-300"></div>
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" 
                  alt="Visa" 
                  className="h-5"
                />
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
                  alt="Mastercard" 
                  className="h-8"
                />
                <div className="w-px h-8 bg-slate-300"></div>
                <img 
                  src="https://badges.razorpay.com/badge-light.png" 
                  alt="Razorpay Trusted Business" 
                  className="h-10"
                />
              </div>
            </div>
          </div>
          
          <div className="border-t border-navy-700 mt-6 pt-8 text-center">
            <p className="text-slate-300 text-sm">© 2025 My Corporate Hub. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Login Dialog */}
      <Dialog open={showLoginDialog} onOpenChange={(open) => {
        setShowLoginDialog(open);
        if (!open) {
          // Reset state when closing
          setLoginMethod('password');
          setOtpSent(false);
          setOtpCode('');
          setLoginEmail('');
          setLoginPassword('');
          setShowPassword(false);
        }
      }}>
        <DialogContent className="sm:max-w-md bg-white">
          {/* Close Button */}
          <button
            onClick={() => setShowLoginDialog(false)}
            className="absolute right-4 top-4 rounded-full p-1 hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-slate-500 hover:text-slate-700" />
          </button>
          
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-navy">Welcome Back</DialogTitle>
            <p className="text-slate-800 text-sm">Sign in to manage your corporate events</p>
          </DialogHeader>
          
          {/* Login Method Toggle */}
          <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
            <button
              onClick={() => {
                setLoginMethod('password');
                setOtpSent(false);
                setOtpCode('');
              }}
              className={`flex-1 py-2 px-4 rounded-md font-medium text-sm transition-all ${
                loginMethod === 'password'
                  ? 'bg-navy text-white shadow'
                  : 'text-slate-800 hover:text-navy'
              }`}
            >
              Password
            </button>
            <button
              onClick={() => {
                setLoginMethod('otp');
                setLoginPassword('');
              }}
              className={`flex-1 py-2 px-4 rounded-md font-medium text-sm transition-all ${
                loginMethod === 'otp'
                  ? 'bg-navy text-white shadow'
                  : 'text-slate-800 hover:text-navy'
              }`}
            >
              Email OTP
            </button>
          </div>

          {loginMethod === 'password' ? (
            /* Password Login Form */
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div>
                <Input
                  type="email"
                  placeholder="Email address"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  className="h-12 bg-white border-gray-300 text-navy placeholder:text-gray-700"
                />
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  className="h-12 bg-white border-gray-300 text-navy placeholder:text-gray-700 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <Button type="submit" className="w-full bg-gold text-navy hover:bg-gold-light h-12 font-semibold" disabled={isLoading}>
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          ) : (
            /* OTP Login Form */
            <div className="space-y-4">
              {!otpSent ? (
                <form onSubmit={handleSendOTP} className="space-y-4">
                  <div>
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="h-12 bg-white border-gray-300 text-navy placeholder:text-gray-700"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gold text-navy hover:bg-gold-light h-12 font-semibold" 
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending OTP...' : 'Send OTP'}
                  </Button>
                  <p className="text-xs text-slate-800 text-center">
                    We'll send a 6-digit code to your email
                  </p>
                </form>
              ) : (
                <form onSubmit={handleVerifyOTP} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Enter 6-digit OTP sent to {loginEmail}
                    </label>
                    <Input
                      type="text"
                      placeholder="000000"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      maxLength={6}
                      className="h-12 bg-white border-gray-300 text-navy placeholder:text-gray-700 text-center text-2xl tracking-widest font-bold"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gold text-navy hover:bg-gold-light h-12 font-semibold" 
                    disabled={isLoading}
                  >
                    {isLoading ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setOtpSent(false);
                      setOtpCode('');
                    }}
                    className="w-full text-sm text-slate-800 hover:text-navy underline"
                  >
                    Change email or resend OTP
                  </button>
                </form>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Chat Widget */}
      {showChat && (
        <div className="fixed bottom-20 right-4 left-4 sm:left-auto sm:right-6 sm:w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-2xl border z-50 overflow-hidden">
          <div className="bg-navy p-3 sm:p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gold rounded-full flex items-center justify-center">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-navy" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm sm:text-base">My Corporate Hub Assistant</p>
                <p className="text-slate-300 text-xs">AI-powered concierge</p>
              </div>
            </div>
            <button onClick={() => setShowChat(false)} className="text-white/70 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {chatMessages.length === 0 && (
              <div className="text-center text-slate-700 py-8">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-navy-300" />
                <p>Hi! How can I help you plan your next corporate event?</p>
              </div>
            )}
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  msg.role === 'user' 
                    ? 'bg-navy text-white' 
                    : 'bg-white border shadow-sm text-slate-900'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isChatLoading && (
              <div className="flex justify-start">
                <div className="bg-white border shadow-sm p-3 rounded-lg">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-navy-300 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-navy-300 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></span>
                    <span className="w-2 h-2 bg-navy-300 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="p-4 border-t bg-white">
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask about venues, events..."
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && handleChatSend()}
              />
              <Button onClick={handleChatSend} className="btn-gold px-4" disabled={isChatLoading}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Chat & WhatsApp Floating Buttons */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col-reverse gap-3">
        {/* WhatsApp Button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={openWhatsApp}
          className="bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full p-3 sm:p-4 shadow-2xl flex items-center justify-center group transition-all duration-300"
          title="Chat on WhatsApp"
        >
          <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        </motion.button>

        {/* Regular Chat Button */}
        {!showChat && (
          <button
            onClick={() => setShowChat(true)}
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gold shadow-lg flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
          >
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 text-navy" />
          </button>
        )}
      </div>

      {/* Fixed Rotating Logo - Bottom Left */}
      <ScrollingLogo />
    </div>
  );
}
